import { Box, Card, CardContent } from '@mui/material'
import React from 'react'

import { CalendarDayProps } from '../../types/ComponentProps'
import Database from '../../types/Database'

import BookingsSection from './BookingsSection'
import DayHeader from './DayHeader'
import NotificationModal from './NotificationModal'

/**
 * CalendarDay - Presentation component for rendering a single calendar day
 *
 * This component focuses solely on UI rendering and user interactions.
 * It receives all necessary data through props and delegates actions
 * to parent components through callbacks.
 *
 * Requirements: 1.1, 6.1, 8.1
 */
export default function CalendarDay({
  date,
  dayOfWeek,
  isToday,
  isPast,
  isWeekend,
  isHighlighted,
  bookings,
  notifications,
  offices,
  hasUserBooking,
  canDelete,
  onDayClick,
  onDeleteClick
}: CalendarDayProps) {
  const [notificationModalOpen, setNotificationModalOpen] =
    React.useState(false)
  const [selectedNotifications, setSelectedNotifications] = React.useState<
    Database.OfficeNotification[]
  >([])

  const handleDayClick = () => {
    onDayClick(date)
  }

  const handleNotificationClick = (notifs: Database.OfficeNotification[]) => {
    setSelectedNotifications(notifs)
    setNotificationModalOpen(true)
  }

  const handleCloseNotificationModal = () => {
    setNotificationModalOpen(false)
  }

  return (
    <>
      <Card
        sx={{
          minHeight: 100,
          height: '100%',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          border: isToday ? '2px solid' : isHighlighted ? '2px solid' : 'none',
          borderColor: isToday
            ? 'primary.main'
            : isHighlighted
            ? 'success.main'
            : 'transparent',
          backgroundColor: isHighlighted
            ? theme =>
                theme.palette.mode === 'dark'
                  ? 'rgba(76, 175, 80, 0.15)'
                  : 'rgba(76, 175, 80, 0.08)'
            : isWeekend
            ? theme =>
                theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.02)'
                  : 'rgba(0, 0, 0, 0.04)'
            : 'background.paper',
          opacity: isPast ? 0.5 : isWeekend ? 0.7 : 1,
          borderRadius: 2,
          boxShadow: isHighlighted ? 3 : 1,
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: theme =>
              isHighlighted
                ? 4
                : theme.palette.mode === 'dark'
                ? '0 0 24px rgba(56, 189, 248, 0.15), 0 8px 16px rgba(0, 0, 0, 0.4)'
                : '0 4px 12px rgba(0,0,0,0.15)',
            borderColor: isHighlighted ? 'success.main' : 'primary.main',
            opacity: isPast ? 0.6 : isWeekend ? 0.85 : 1
          }
        }}
        onClick={handleDayClick}
      >
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <DayHeader
            date={date}
            dayOfWeek={dayOfWeek}
            isToday={isToday}
            hasUserBooking={hasUserBooking}
            canDelete={canDelete}
            notifications={notifications}
            onDeleteClick={onDeleteClick}
            onNotificationClick={handleNotificationClick}
          />

          <Box sx={{ width: '100%', mt: 0.5 }}>
            <BookingsSection bookings={bookings} />
          </Box>
        </CardContent>
      </Card>

      <NotificationModal
        open={notificationModalOpen}
        notifications={selectedNotifications}
        offices={offices}
        onClose={handleCloseNotificationModal}
      />
    </>
  )
}
